import type { VueI18nTranslation } from 'vue-i18n';

import type { Accommodation } from '~~/domains/graphql';
import type { IconNameSpace } from '~~/domains/ux/atoms/AiIcon/types';

export type Amenity = {
  icon?: string;
  iconNamespace?: IconNameSpace;
  label: string;
  key: string;
};

/**
 * @see https://jira.kaliop.net/browse/ACCORDAI-97
 */
export function getSortedRoomAmenities(
  room?: Accommodation,
  t?: VueI18nTranslation,
): Amenity[] {
  if (!room || !t) return [];

  const amenities = [] as Amenity[];

  addRoomSurface(room, amenities);
  addOccupancy(room, amenities, t);
  addBeddings(room, amenities, t);
  addView(room, amenities);

  return amenities;
}

/**
 * Add bedding capabilities to the amenities array
 */
function addBeddings(
  room: Accommodation,
  amenities: Amenity[],
  t: VueI18nTranslation,
): void {
  const configuration = room.configuration?.[0];

  if (!configuration) return;

  const { qualified } = configuration.beddings ?? {};

  if (!Array.isArray(qualified) || qualified.length <= 0) return;

  const separator = t('hotel.room.amenities.bedding.separator');
  const label = qualified
    .map(
      qualifiedBedding =>
        `${qualifiedBedding?.quantity} ${qualifiedBedding?.label}`,
    )
    .join(` ${separator} `);

  amenities.push({
    icon: 'double-bed',
    key: 'beddings',
    label,
  });
}

/**
 * Add occupancy capabilities to the amenities array
 */
function addOccupancy(
  room: Accommodation,
  amenities: Amenity[],
  t: VueI18nTranslation,
): void {
  const configuration = room.configuration?.[0];

  if (!configuration) return;

  const { maxOccupancy } = configuration ?? {};

  if (maxOccupancy?.pax) {
    amenities.push({
      icon: 'occupant-child',
      key: 'occupancy',
      label: t('hotel.room.amenities.occupancy', { count: maxOccupancy.pax }),
    });
  }
}

/**
 * Add the room surface to the amenities array
 * @param room
 * @param amenities
 */
function addRoomSurface(room: Accommodation, amenities: Amenity[]): void {
  if (room.surface?.squareFeet || room.surface?.squareMeter) {
    // @todo - Determine which unit to use based on user ?
    amenities.push({
      icon: 'size',
      key: 'surface',
      label: `${room.surface.squareMeter}m²`,
    });
  }
}

function addView(room: Accommodation, amenities: Amenity[]): void {
  const view = room.configuration?.[0]?.situation?.views?.join(' - ');

  if (view) {
    amenities.push({
      icon: 'mountain',
      iconNamespace: 'amenities',
      key: 'view',
      label: view,
    });
  }
}
